// Мост к нативной оболочке: приложения com.cloudGPS.client (iOS) и
// com.cloudgps.client (Android).
//
// В браузере всё это — no-op: hasNative() === false, и портал работает ровно как
// раньше. Внутри приложения появляется window.__CGPS_NATIVE__, и становятся
// доступны вещи, которых у сайта быть не может: пуши APNs/FCM, вход по лицу
// или отпечатку, «Поделиться», отклик на нажатие.
//
// Канал в нативную часть у платформ разный: на iOS это WKScriptMessageHandler
// (window.webkit.messageHandlers.cgps), на Android — объект window.cgpsAndroid,
// который оболочка вешает только на страницы своего домена. Android-канал
// принимает строки, поэтому сообщение уходит туда JSON-ом.
//
// Протокол: в нативную часть уходит { id, type, ...params }, ответ приходит
// вызовом window.__cgpsResolve(id, payload). Свои ошибки оболочка помечает
// ключом nativeError — поле error остаётся за бэкендом, иначе настоящую
// причину сбоя не отличить от отказа API.

export const hasNative = () => typeof window !== 'undefined' && !!window.__CGPS_NATIVE__
export const nativePlatform = () => (typeof window !== 'undefined' && window.__CGPS_PLATFORM__) || null

let seq = 0
const pending = new Map()

if (typeof window !== 'undefined') {
  window.__cgpsResolve = (id, payload) => {
    const p = pending.get(id)
    if (!p) return
    pending.delete(id)
    if (payload && payload.nativeError) p.reject(new Error(String(payload.nativeError)))
    else p.resolve(payload)
  }
}

export function call(type, params = {}) {
  if (!hasNative()) return Promise.reject(new Error('нативная оболочка недоступна'))
  return new Promise((resolve, reject) => {
    const id = ++seq
    pending.set(id, { resolve, reject })
    try {
      const msg = { id, type, ...params }
      const android = window.cgpsAndroid
      if (android && typeof android.postMessage === 'function') android.postMessage(JSON.stringify(msg))
      else window.webkit.messageHandlers.cgps.postMessage(msg)
    } catch (e) {
      pending.delete(id)
      return reject(new Error('мост не отвечает: ' + String((e && e.message) || e)))
    }
    // Висеть вечно нельзя: разрешение на уведомления человек может просто не закрыть.
    setTimeout(() => {
      if (pending.delete(id)) reject(new Error('таймаут моста'))
    }, 30000)
  })
}

// Мягкие обёртки: в браузере возвращают безопасный ответ, а не падают, —
// чтобы вызывающий код не оброс проверками hasNative() в каждой строке.
const soft = (fn, fallback) => async (...args) => {
  if (!hasNative()) return fallback
  try { return await fn(...args) } catch { return fallback }
}

export const native = {
  ping: soft(() => call('ping'), null),

  // Токен пушей: { token, env, device } (APNs на iOS, FCM на Android, env там
  // всегда 'fcm'). Может показать системный запрос
  // разрешения, поэтому вызывать только по нажатию. Регистрируем его на
  // сервере ОТСЮДА, авторизованным запросом портала: у оболочки своей сессии нет.
  pushToken: soft(() => call('pushToken').then((r) => (r && r.token ? r : null)), null),
  // Состояние без запроса разрешения: { status, token?, env, device }.
  // status: authorized | denied | notDetermined. Токен — только если разрешено.
  pushStatus: soft(() => call('pushStatus'), null),

  biometryStatus: soft(() => call('biometryStatus'), { available: false, enabled: false, kind: 'none' }),
  biometryEnable: soft(() => call('biometryEnable').then((r) => !!(r && r.ok)), false),
  biometryDisable: soft(() => call('biometryDisable').then(() => true), false),

  // Настоящий выход: оболочка чистит куки и данные сайта, иначе сессия
  // переживает «выйти» и человек возвращается уже вошедшим.
  logout: soft(() => call('logout'), null),

  host: soft(() => call('host'), null),
  share: soft((text) => call('share', { text }), null),
  haptic: soft((kind) => call('haptic', { kind }), null),
}
